"use client";

import { ListingSearchData } from "@/lib/types/search";
import React from "react";
import Image from "next/image";
import { useFormatter, useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { ChevronRight, DoorOpen, Expand, Sofa, X } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { DEFAULT_IMAGE } from "@/lib/data/images";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import ListingPromotionBadge from "./ListingPromotionBadge";
import ListingPublisherByline from "./ListingPublisherByline";
import EnergyChip from "./EnergyChip";
import ListingViews from "./ListingViews";

type Props = {
  item: ListingSearchData;
  className?: string;
  onClose?: () => void;
};

/**
 * Horizontal listing card driven purely by container queries — it adapts to
 * the space it is given, not the screen. The @container root lives inside the
 * component so every render site gets the responsive behavior for free.
 *
 * Tiers: base (<32rem) compact row · @lg (≥32rem) chips + 2-line title +
 * "See details" button · @2xl (≥42rem) large photo and roomier type.
 */
const ListingSearchItem: React.FC<Props> = ({ item, className, onClose }) => {
  const t = useTranslations("listing");
  const formatter = useFormatter();

  return (
    <div className={twMerge("google-anno-skip @container", className)}>
      <div className="group relative grid h-full grid-cols-[128px_minmax(0,1fr)] gap-2.5 rounded-2xl border border-border bg-background p-2 shadow-sm transition-[box-shadow,border-color] hover:border-zinc-300 hover:shadow-[0_10px_28px_-16px_rgba(24,24,27,0.45)] @lg:grid-cols-[176px_minmax(0,1fr)] @lg:gap-3 @lg:p-2.5 @2xl:grid-cols-[236px_minmax(0,1fr)] @2xl:gap-4">
        {/* The photo fills the full card height so it stays the visual anchor. */}
        <div className="relative min-h-[108px] overflow-hidden rounded-xl border border-input @lg:min-h-[136px] @2xl:min-h-[168px]">
          <ListingPromotionBadge
            promotion={item.promotion}
            className="absolute left-2 top-2 z-10"
          />
          <Image
            src={item.imageUrl || DEFAULT_IMAGE.LISTING}
            alt={item.title}
            width={500}
            height={375}
            unoptimized
            sizes={IMAGE_SIZES.cardMedium}
            className="absolute inset-0 h-full w-full object-cover"
          />
          {onClose && (
            <button
              aria-label={t("close")}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="absolute z-20 right-1.5 top-1.5 inline-flex size-8 items-center justify-center rounded-full border border-border bg-background/90 text-foreground transition-colors hover:bg-background"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="flex min-w-0 flex-col py-0.5 pr-1 @lg:py-1">
          <div className="flex flex-row items-start justify-between gap-2">
            <Link
              href={item.href}
              title={item.title}
              className="after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-accent line-clamp-1 text-[13px] font-bold leading-snug text-blue-700 @lg:line-clamp-2 @lg:text-sm @2xl:text-base"
            >
              {item.title}
            </Link>
            {item.attributes.energyLabel && (
              <EnergyChip
                label={item.attributes.energyLabel}
                className="relative z-10"
              />
            )}
          </div>
          <ListingPublisherByline publisher={item.publisher} />
          {item.isAvailable === false && (
            <p className="text-xs font-medium text-amber-800">
              {t("unavailable")}
            </p>
          )}
          <p className="truncate text-[11px] text-muted-foreground @lg:text-xs">
            {item.location.postalCode
              ? `${item.location.postalCode} · ${item.location.city}`
              : item.location.city}
          </p>
          <p className="mt-0.5 text-foreground @2xl:mt-1">
            <span className="text-[15px] font-bold @lg:text-base @2xl:text-lg">
              {formatter.number(item.attributes.price, {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 0,
              })}
            </span>
            <span className="text-[11px] text-muted-foreground @lg:text-xs">
              {t("price-month")}
            </span>
          </p>

          {/* compact tier: inline meta */}
          <div className="mt-0.5 flex flex-row flex-wrap gap-x-2.5 gap-y-0.5 text-[11px] text-zinc-700 @lg:hidden">
            <MetaInline
              icon={<Expand className="size-3" strokeWidth={1.5} />}
              text={t("size", { size: item.attributes.size })}
            />
            <MetaInline
              icon={<DoorOpen className="size-3" strokeWidth={1.5} />}
              text={t("rooms", { count: item.attributes.roomsCount })}
            />
            <MetaInline
              icon={<Sofa className="size-3" strokeWidth={1.5} />}
              text={t(`furnished.${item.attributes.furnishedType}`)}
            />
          </div>

          {/* @lg+: meta chips */}
          <div className="mt-1.5 hidden flex-wrap gap-1.5 @lg:flex">
            <MetaChip
              icon={<Expand className="size-3.5" strokeWidth={1.5} />}
              text={t("size", { size: item.attributes.size })}
            />
            <MetaChip
              icon={<DoorOpen className="size-3.5" strokeWidth={1.5} />}
              text={t("rooms", { count: item.attributes.roomsCount })}
            />
            <MetaChip
              icon={<Sofa className="size-3.5" strokeWidth={1.5} />}
              text={t(`furnished.${item.attributes.furnishedType}`)}
            />
          </div>

          <div className="mt-auto flex items-end justify-end gap-3 pt-1 @lg:items-center @lg:pt-2">
            {item.views && (
              <span className="relative z-10 inline-flex h-4 items-center @lg:h-5">
                <ListingViews views={item.views} />
              </span>
            )}
            {!item.views && (
              <ChevronRight
                aria-hidden
                className="size-4 text-zinc-400 @lg:hidden"
              />
            )}
            <Link
              href={item.href}
              className="relative z-10 hidden h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-secondary px-3.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 @lg:inline-flex @2xl:h-9 @2xl:rounded-xl @2xl:px-4"
            >
              <span>{t("see-details")}</span>
              <ChevronRight aria-hidden className="size-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingSearchItem;

type MetaProps = {
  icon: React.ReactNode;
  text: string;
};

const MetaInline: React.FC<MetaProps> = ({ icon, text }) => {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="text-muted-foreground">{icon}</span>
      {text}
    </span>
  );
};

const MetaChip: React.FC<MetaProps> = ({ icon, text }) => {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-2.5 py-1 text-[11px] text-zinc-700">
      <span className="text-muted-foreground">{icon}</span>
      {text}
    </span>
  );
};
